import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, ShieldCheck, AlertTriangle, CheckCircle, FileText, Scale, Eye, Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_ICON = {
  pass: <CheckCircle className="h-4 w-4 text-green-600" />,
  warn: <AlertTriangle className="h-4 w-4 text-amber-600" />,
  fail: <AlertTriangle className="h-4 w-4 text-red-600" />,
};

const RATING_PALETTE = {
  Compliant: 'bg-green-50 border-green-300 text-green-900',
  'Near-compliant': 'bg-amber-50 border-amber-300 text-amber-900',
  Gaps: 'bg-red-50 border-red-300 text-red-900',
};

export const AdminAMLTab = () => {
  const { token } = useAuth();
  const [report, setReport] = useState(null);
  const [audit, setAudit] = useState([]);
  const [sars, setSars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [screenName, setScreenName] = useState('');
  const [screenDob, setScreenDob] = useState('');
  const [screenResult, setScreenResult] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a, s] = await Promise.all([
        axios.get(`${API_URL}/api/admin/aml/compliance-report`, auth),
        axios.get(`${API_URL}/api/admin/aml/audit-log?limit=50`, auth),
        axios.get(`${API_URL}/api/admin/aml/sars`, auth),
      ]);
      setReport(r.data);
      setAudit(a.data.items || []);
      setSars(s.data.items || []);
    } catch {
      toast.error('Failed to load AML data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const runScreen = async () => {
    if (!screenName.trim()) return toast.error('Enter a full name');
    try {
      const res = await axios.post(`${API_URL}/api/aml/screen`,
        { full_name: screenName, dob_year: screenDob ? parseInt(screenDob) : undefined },
        auth);
      setScreenResult(res.data.result);
      toast[res.data.result.status === 'clear' ? 'success' : 'warning'](
        `Result: ${res.data.result.status.toUpperCase()}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Screening failed');
    }
  };

  if (loading) return <div className="luxury-card p-10 text-center"><Loader2 className="h-8 w-8 animate-spin text-[#E07A3A] mx-auto" /></div>;
  if (!report) return null;

  const paletteClass = RATING_PALETTE[report.rating] || RATING_PALETTE.Gaps;

  return (
    <div className="space-y-6" data-testid="admin-aml-tab">
      {/* UKSL sync card */}
      <UKSLSyncCard token={token} />

      {/* Score card */}
      <div className={`${paletteClass} border p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6`} data-testid="aml-score-card">
        <div className="flex-shrink-0">
          <ShieldCheck className="h-10 w-10" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold opacity-75">Platform AML Compliance</p>
          <p className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold mt-1">
            {report.score_pct}% <span className="text-base sm:text-lg font-normal opacity-80">· {report.rating}</span>
          </p>
          <p className="text-xs opacity-80 mt-1">Regulators: {Object.values(report.regulators).join(' · ')}</p>
        </div>
        <button type="button" onClick={load}
          className="inline-flex items-center gap-1.5 bg-white/60 hover:bg-white border border-current/30 px-3 py-2 text-xs font-semibold">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* Checks */}
      <div className="luxury-card p-6" data-testid="aml-checks">
        <h3 className="font-['Playfair_Display'] text-xl text-[#0F172A] mb-4">Compliance checks</h3>
        <ul className="space-y-2">
          {report.checks.map(c => (
            <li key={c.id} className="flex items-center gap-3 p-3 bg-[#F8FAFC] border border-slate-200" data-testid={`aml-check-${c.id}`}>
              {STATUS_ICON[c.status]}
              <span className="text-sm text-[#0F172A] flex-1">{c.label}</span>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${c.status === 'pass' ? 'bg-emerald-950/95 text-emerald-300 border-emerald-500/50' : 'bg-amber-950/95 text-amber-300 border-amber-500/50'}`}>{c.status}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Metrics grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="aml-metrics">
        {[
          { label: 'Total users', value: report.metrics.total_users },
          { label: 'ID verified', value: report.metrics.id_verified },
          { label: 'Source of Funds declared', value: report.metrics.sof_declared },
          { label: 'Sanctions-list screenings', value: report.metrics.screened },
          { label: 'Sanctions matches', value: report.metrics.screening_sanctions, highlight: 'red' },
          { label: 'PEP matches', value: report.metrics.screening_pep, highlight: 'amber' },
          { label: 'Open SARs', value: report.metrics.sars_open, highlight: 'red' },
          { label: 'Audit trail entries', value: report.metrics.audit_entries },
        ].map(m => (
          <div key={m.label} className="p-4 bg-white border border-slate-200">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{m.label}</p>
            <p className={`font-['Playfair_Display'] text-2xl mt-1 ${m.highlight === 'red' && m.value > 0 ? 'text-red-600' : m.highlight === 'amber' && m.value > 0 ? 'text-amber-600' : 'text-[#0F172A]'}`}>{m.value}</p>
          </div>
        ))}
      </div>

      {/* Ad-hoc name screening */}
      <div className="luxury-card p-6" data-testid="aml-screener">
        <div className="flex items-center gap-2 mb-3"><Search className="h-4 w-4 text-[#E07A3A]" /><h3 className="font-['Playfair_Display'] text-xl text-[#0F172A]">Screen a name</h3></div>
        <p className="text-xs text-slate-500 mb-4">Check any full name against UK HMT, OFAC, UN and PEP lists.</p>
        <div className="grid sm:grid-cols-[2fr_1fr_auto] gap-2">
          <input type="text" value={screenName} onChange={(e) => setScreenName(e.target.value)}
            placeholder="Full legal name"
            className="bg-[#F8FAFC] border border-slate-200 h-11 px-3 text-sm" data-testid="screen-name-input" />
          <input type="number" value={screenDob} onChange={(e) => setScreenDob(e.target.value)}
            placeholder="DOB year (optional)"
            className="bg-[#F8FAFC] border border-slate-200 h-11 px-3 text-sm" data-testid="screen-dob-input" />
          <button type="button" onClick={runScreen}
            className="bg-[#0F172A] text-white hover:bg-[#1e293b] h-11 px-5 text-xs font-semibold"
            data-testid="screen-run-btn">Run screening</button>
        </div>

        {screenResult && (
          <div className={`mt-4 p-3 text-sm border ${screenResult.status === 'clear' ? 'bg-green-50 border-green-300 text-green-900' : 'bg-red-50 border-red-300 text-red-900'}`} data-testid="screen-result">
            <p className="font-bold uppercase text-xs tracking-wider mb-1">Result: {screenResult.status} · risk {screenResult.risk}</p>
            {screenResult.matches.length > 0 && (
              <ul className="text-xs space-y-1 mt-2">
                {screenResult.matches.map((m, i) => (
                  <li key={`m-${i}`}>• <b>{m.name}</b> — {m.reason || m.role} ({m.list || m.country}) [{m.list_type}]</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* SARs */}
      <div className="luxury-card p-6" data-testid="aml-sars">
        <div className="flex items-center gap-2 mb-3"><FileText className="h-4 w-4 text-[#E07A3A]" /><h3 className="font-['Playfair_Display'] text-xl text-[#0F172A]">Suspicious Activity Reports</h3></div>
        {sars.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No SARs submitted.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {sars.slice(0, 10).map(s => (
              <li key={s.id} className="py-3 text-sm">
                <p className="font-semibold text-[#0F172A]">{s.reason}</p>
                <p className="text-xs text-slate-500">Subject #{s.subject_user_id} · {s.regulator} · {s.submitted_at?.slice(0, 10)} · <span className="uppercase">{s.status}</span></p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Audit log */}
      <div className="luxury-card p-6" data-testid="aml-audit-log">
        <div className="flex items-center gap-2 mb-3"><Scale className="h-4 w-4 text-[#E07A3A]" /><h3 className="font-['Playfair_Display'] text-xl text-[#0F172A]">Immutable audit trail</h3></div>
        {audit.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No events logged yet. Every SOF declaration, screening and SAR will appear here with SHA-256 hash-chain.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Event</th>
                  <th className="py-2 pr-3">Subject</th>
                  <th className="py-2 pr-3">Severity</th>
                  <th className="py-2 pr-3">IP</th>
                  <th className="py-2 pr-3">Hash</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {audit.slice(0, 20).map(e => (
                  <tr key={e.id} className="text-slate-700">
                    <td className="py-2 pr-3 whitespace-nowrap">{e.created_at?.slice(0, 19).replace('T', ' ')}</td>
                    <td className="py-2 pr-3 font-mono">{e.event_type}</td>
                    <td className="py-2 pr-3 font-mono text-[10px]">{(e.user_id || '').slice(-8)}</td>
                    <td className="py-2 pr-3">
                      <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${e.severity === 'critical' ? 'bg-red-950/95 text-red-300 border-red-500/50' : e.severity === 'warning' ? 'bg-amber-950/95 text-amber-300 border-amber-500/50' : 'bg-slate-900 text-slate-300 border-slate-500/40'}`}>{e.severity}</span>
                    </td>
                    <td className="py-2 pr-3 font-mono text-[10px]">{e.ip}</td>
                    <td className="py-2 pr-3 font-mono text-[10px]" title={e.hash}>{(e.hash || '').slice(0, 10)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAMLTab;

const UKSLSyncCard = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/aml/sanctions/status`, auth);
      setStatus(res.data);
    } catch (e) { /* keep silent */ }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const runSync = async () => {
    if (!window.confirm('Fetch the latest UK Sanctions List from FCDO? Runs in the background — typically completes in 5-15 seconds.')) return;
    setSyncing(true);
    const previouslyFinishedAt = status?.last_sync?.finished_at || null;
    try {
      await axios.post(`${API_URL}/api/admin/aml/sanctions/sync`, {}, { ...auth, timeout: 30000 });
      toast.info('UKSL sync queued — checking for completion...');
      // Poll status endpoint up to 24 times × 5s = 2 minutes max
      let polls = 0;
      const maxPolls = 24;
      const interval = setInterval(async () => {
        polls += 1;
        try {
          const res = await axios.get(`${API_URL}/api/admin/aml/sanctions/status`, auth);
          const newFinished = res.data?.last_sync?.finished_at;
          if (newFinished && newFinished !== previouslyFinishedAt) {
            clearInterval(interval);
            setStatus(res.data);
            const last = res.data.last_sync || {};
            if (last.status === 'ok' || last.status === 'warning') {
              toast.success(`UKSL synced — ${last.fetched?.toLocaleString() || 0} rows in ${last.duration_s || '?'}s`);
            } else {
              toast.error(`Sync failed: ${(last.error || 'unknown error').slice(0, 120)}`);
            }
            setSyncing(false);
          } else if (polls >= maxPolls) {
            clearInterval(interval);
            toast.warning('Sync still running after 2 min — refresh page to check status.');
            setSyncing(false);
          }
        } catch (e) { /* keep polling */ }
      }, 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to queue sync');
      setSyncing(false);
    }
  };

  if (loading) return null;
  const staleHours = status?.last_sync?.finished_at
    ? Math.round((Date.now() - new Date(status.last_sync.finished_at)) / 3600000)
    : null;
  const stale = staleHours == null || staleHours > 48;

  return (
    <div className={`${stale ? 'bg-amber-50 border-amber-300' : 'bg-green-50 border-green-300'} border p-4 space-y-3`} data-testid="uksl-sync-card">
      <div className="flex flex-wrap items-center gap-3">
        <ShieldCheck className={`${stale ? 'text-amber-700' : 'text-green-700'} h-5 w-5 flex-shrink-0`} />
        <div className="flex-1 min-w-[220px]">
          <p className={`${stale ? 'text-amber-900' : 'text-green-900'} text-sm font-semibold`}>
            UK Sanctions List (UKSL) feed · {status?.feed_size?.toLocaleString() || 0} designated persons
          </p>
          <p className={`${stale ? 'text-amber-800' : 'text-green-800'} text-xs mt-0.5`}>
            {status?.last_sync?.finished_at
              ? `Last synced ${staleHours}h ago from sanctionslist.fcdo.gov.uk`
              : 'Never synced — click Sync to fetch the live feed'}
            {status?.last_sync?.status === 'warning' && ' · ⚠ warnings in last sync'}
            {status?.last_sync?.status === 'error' && ' · 🚨 last sync FAILED'}
          </p>
        </div>
        <button type="button" onClick={runSync} disabled={syncing}
          className="bg-[#0F172A] text-white hover:bg-[#1e293b] px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
          data-testid="uksl-sync-btn">
          {syncing ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Syncing…</> : <><RefreshCw className="h-3.5 w-3.5" /> Sync UKSL now</>}
        </button>
      </div>

      {/* Compliance disclaimer + GOV.UK subscription link */}
      <div className="flex items-start gap-2 bg-white/70 border border-current/20 p-3 text-[11px] leading-relaxed text-slate-700">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-700 mt-0.5 flex-shrink-0" />
        <div>
          <p className="mb-1">
            <b>Compliance note:</b> The UKSL feed is free but <b>you retain MLR 2017 liability</b> if an automated sync fails silently and you miss a new designation.
            Shopperden emails the admin on every sync failure and if the feed goes stale for &gt;36h, but please also subscribe to the official GOV.UK alerts as a backup.
          </p>
          <a href="https://www.gov.uk/government/collections/uk-sanctions-lists"
             target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 text-[#0F172A] font-semibold hover:text-[#E07A3A]"
             data-testid="uksl-govuk-subscribe">
            → Subscribe to GOV.UK email alerts for UKSL updates
          </a>
        </div>
      </div>
    </div>
  );
};
